import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
  useTransition,
} from 'react';
import {ActionType} from 'typesafe-actions';
import {UserInterfaceActions, UserInterfaceActionTypes} from './UserInterfaceActionTypes';
import {SelectedSide, SideStatus, ViewMode} from '@taopix/taopix-core';
import {useExperience} from '../../Experience/ExperienceProvider';
import {DialogStatus, ThreeDPreviewStatus} from '../../../Enums';
import {SortOrder, SortDirection, PageInterface} from '@taopix/taopix-core/dist/types/types';
import {GridSizes} from '../../GridSizeSelector/GridSizeSelector';
import useSettings from '../../../Hooks/useSettings';
import {useTaopix} from '../TaopixContext';

export type RootAction = ActionType<typeof import('./UserInterfaceActionTypes')>;

export type ToolPaletteState = {
  section: string | null;
  dirty: boolean;
  requestedPath: string;
  confirmationRequired: boolean;
  picturePallette: {
    sortOrder: SortOrder;
    sortDirection: SortDirection;
  };
};

export type UiDialogs = {
  pageCountChangeNotification: DialogStatus;
  coverSpineChangeNotification: DialogStatus;
  picturesIgnoredBySmartDesignNotification: DialogStatus;
  projectLayoutChangeNotification: DialogStatus;
  alternativeProductsUnavailable: DialogStatus;
  submittedChangesNotification: DialogStatus;
};

export type UIFlags = {
  pageCountChanged: boolean;
  coverNeedsChecking: boolean;
  recommendAddingPictures: boolean;
};

export type AutoFillOptions = {
  autoFillDialog: boolean;
  autoFillCompleted: boolean;
  autoFillNotAllPicturesUsed: boolean;
  recommendedPageCount: number;
  recommendedPageCountPrice: string;
  maxPageCount: number;
  maxPageCountPrice: string;
};

export type SelectedSideData = {
  backgroundAssetId: string;
  pageStyleRef: number;
  pageNumber: number;
  side: SelectedSide;
  status: SideStatus;
  imageMissing: boolean;
};

export type KeyboardHelpState = {
  open: boolean;
  currentTip: string | null;
};

export type UserInterFaceTipsState = {
  selectPictureForEmptyBox: boolean;
  arrangePicturesByApplyingLayout: boolean;
  openLayoutsPalette: boolean;
  useAutofill: boolean;
  toolPaletteIntro: boolean;
  gridViewIntro: boolean;
  keyboardHelp: KeyboardHelpState;
};

export type UserInterfaceState = {
  pageNumber: number;
  lastViewedPageNumber?: number;
  languageDialogOpen: boolean;
  pagestyleErrorTip: {open: boolean; errorString: string};
  palette: ToolPaletteState;
  focusMode: boolean;
  isSmallScreen: boolean;
  viewOffset: number;
  children?: any;
  busy: boolean;
  calendarCloseDialog?: boolean;
  calendarEditPictureDialog: boolean;
  viewMode: ViewMode;
  threeDPreviewStatus: ThreeDPreviewStatus;
  dialogs: UiDialogs;
  flags: UIFlags;
  autoFillOptions: AutoFillOptions;
  canEditPageLayout: boolean;
  zoomLevel: number;
  projectNaming: {previousProjectName: string; projectName: string; editName: boolean};
  savePrompt: boolean;
  saveCount: number;
  gridSize: GridSizes | null;
  autoGridSizeSwitching: boolean;
  previewViewMode: ViewMode;
  projectChanged: boolean;
  calendarApplyLayoutsWarningDialog: boolean;
  selectedSide: SelectedSideData;
  tips: UserInterFaceTipsState;
  fromAssistant: {
    fromAssistant: boolean;
    assistantRan: boolean;
  };
  swapActive: boolean;
  history: {
    undo: boolean;
    redo: boolean;
  };
  insideCoverNotEditableTip: {
    open: boolean;
    message: string;
    title: string;
  };
  tempUILayoutType: 'myikona' | 'taopix';
  textIsProcessing: boolean;
  gridViewColumnData: {
    columns: number; // the number of columns in a row in gridview
    sortedPages: PageInterface[]; // the sorted pages in grid view
  };
};

export type UserContextState = {
  state: UserInterfaceState;
  dispatch: React.Dispatch<RootAction>;
};

const initialState: UserInterfaceState = {
  busy: false,
  pageNumber: -1,
  lastViewedPageNumber: -1,
  languageDialogOpen: false,
  pagestyleErrorTip: {open: false, errorString: ''},
  palette: {
    section: null,
    dirty: false,
    requestedPath: '',
    confirmationRequired: false,
    picturePallette: {
      sortOrder: 'dt',
      sortDirection: 'asc',
    },
  },
  focusMode: false,
  isSmallScreen: false,
  viewOffset: 0,
  calendarCloseDialog: false,
  calendarEditPictureDialog: false,
  viewMode: ViewMode.None,
  threeDPreviewStatus: ThreeDPreviewStatus.None,
  dialogs: {
    pageCountChangeNotification: DialogStatus.Closed,
    coverSpineChangeNotification: DialogStatus.Closed,
    picturesIgnoredBySmartDesignNotification: DialogStatus.Closed,
    projectLayoutChangeNotification: DialogStatus.Closed,
    alternativeProductsUnavailable: DialogStatus.Closed,
    submittedChangesNotification: DialogStatus.Closed,
  },
  flags: {
    pageCountChanged: false,
    coverNeedsChecking: false,
    recommendAddingPictures: false,
  },
  autoFillOptions: {
    autoFillDialog: false,
    autoFillCompleted: false,
    autoFillNotAllPicturesUsed: false,
    recommendedPageCount: 0,
    recommendedPageCountPrice: '',
    maxPageCount: 0,
    maxPageCountPrice: '',
  },
  canEditPageLayout: false,
  zoomLevel: 100,
  projectNaming: {previousProjectName: '', projectName: '', editName: true},
  savePrompt: false,
  saveCount: 0,
  gridSize: null,
  autoGridSizeSwitching: true,
  previewViewMode: ViewMode.List,
  projectChanged: false,
  calendarApplyLayoutsWarningDialog: false,
  selectedSide: {
    backgroundAssetId: '',
    pageNumber: 0,
    side: SelectedSide.Left,
    pageStyleRef: 0,
    status: SideStatus.Passive,
    imageMissing: false,
  },
  tips: {
    selectPictureForEmptyBox: false,
    arrangePicturesByApplyingLayout: false,
    openLayoutsPalette: false,
    useAutofill: false,
    toolPaletteIntro: false,
    gridViewIntro: false,
    keyboardHelp: {
      open: false,
      currentTip: null,
    },
  },
  fromAssistant: {
    fromAssistant: false,
    assistantRan: false,
  },
  swapActive: false,
  history: {
    undo: false,
    redo: false,
  },
  insideCoverNotEditableTip: {
    open: false,
    message: '',
    title: '',
  },
  tempUILayoutType: 'myikona',
  textIsProcessing: false,
  gridViewColumnData: {
    columns: 0,
    sortedPages: [],
  },
};

export const UserInterfaceContext = createContext<UserContextState>({} as UserContextState);

const userInterfaceReducer = (
  state: UserInterfaceState,
  action: RootAction
): UserInterfaceState => {
  switch (action.type) {
    case UserInterfaceActionTypes.SET_PAGE_NUMBER: {
      return {
        ...state,
        pageNumber: action.payload,
      };
    }
    case UserInterfaceActionTypes.SET_LANGUAGE_DIALOG_OPEN: {
      return {
        ...state,
        languageDialogOpen: action.payload,
      };
    }
    case UserInterfaceActionTypes.SET_IS_BUSY: {
      return {
        ...state,
        busy: action.payload,
      };
    }
    case UserInterfaceActionTypes.SET_TOOL_PALETTE_SECTION: {
      // if dirty and requested path is not a child of the current path then update requested path
      if (
        state.palette.dirty &&
        action.payload &&
        ((!action.payload.includes(state.palette.section as string) &&
          state.palette.section &&
          !state.palette.section.includes(action.payload)) ||
          state.palette.confirmationRequired ||
          action.payload === 'calendar-palette')
      ) {
        return {
          ...state,
          palette: {
            ...state.palette,
            requestedPath: action.payload as string,
            confirmationRequired: true,
          },
        };
      }
      return {
        ...state,
        palette: {
          ...state.palette,
          section: action.payload,
          requestedPath: '',
          confirmationRequired: false,
        },
      };
    }
    case UserInterfaceActionTypes.SET_PALETTE_SECTION_DIRTY: {
      return {
        ...state,
        palette: {
          ...state.palette,
          dirty: action.payload,
        },
      };
    }
    case UserInterfaceActionTypes.SET_PALETTE_STATE: {
      return {
        ...state,
        palette: {
          ...state.palette,
          ...action.payload,
        },
      };
    }
    case UserInterfaceActionTypes.SET_PICTURE_PALETTE_SORTORDER: {
      const newState = state;
      newState.palette = {
        ...state.palette,
        picturePallette: {
          ...state.palette.picturePallette,
          sortOrder: action.payload.sortOrder,
          sortDirection: action.payload.sortDirection,
        },
      };
      return newState;
    }
    case UserInterfaceActionTypes.SET_PAGESTYLE_ERROR_TIP: {
      return {
        ...state,
        pagestyleErrorTip: {...state.pagestyleErrorTip, ...action.payload},
      };
    }
    case UserInterfaceActionTypes.SET_FOCUS_MODE: {
      return {
        ...state,
        focusMode: action.payload,
      };
    }
    case UserInterfaceActionTypes.SET_IS_SMALL_SCREEN: {
      return {
        ...state,
        isSmallScreen: action.payload,
      };
    }
    case UserInterfaceActionTypes.SET_VIEW_OFFSET: {
      return {
        ...state,
        viewOffset: action.payload,
      };
    }
    case UserInterfaceActionTypes.CALENDAR_EDIT_PICTURE_DIALOG: {
      return {
        ...state,
        calendarEditPictureDialog: action.payload,
      };
    }
    case UserInterfaceActionTypes.SET_VIEW_MODE: {
      return {
        ...state,
        viewMode: action.payload,
      };
    }
    case UserInterfaceActionTypes.SET_3DPREVIEW_STATUS: {
      return {
        ...state,
        threeDPreviewStatus: action.payload,
      };
    }
    case UserInterfaceActionTypes.SET_DIALOG_STATUS: {
      return {
        ...state,
        dialogs: {
          ...state.dialogs,
          ...action.payload,
        },
      };
    }
    case UserInterfaceActionTypes.SET_FLAGS: {
      return {
        ...state,
        flags: {
          ...state.flags,
          ...action.payload,
        },
      };
    }
    case UserInterfaceActionTypes.SET_PAGE_LOCK: {
      return {
        ...state,
        canEditPageLayout: action.payload,
      };
    }
    case UserInterfaceActionTypes.SET_AUTOFILL_OPTIONS: {
      return {
        ...state,
        autoFillOptions: {
          ...state.autoFillOptions,
          ...action.payload,
        },
      };
    }
    case UserInterfaceActionTypes.SET_ZOOM_LEVEL: {
      return {
        ...state,
        zoomLevel: action.payload,
      };
    }
    case UserInterfaceActionTypes.SET_PROJECT_NAME_DIALOG: {
      return {
        ...state,
        projectNaming: {
          ...state.projectNaming,
          ...action.payload,
        },
      };
    }
    case UserInterfaceActionTypes.SET_SAVE_PROMPT: {
      return {
        ...state,
        savePrompt: action.payload,
      };
    }
    case UserInterfaceActionTypes.SET_SAVE_COUNT: {
      return {
        ...state,
        saveCount: action.payload,
      };
    }
    case UserInterfaceActionTypes.SET_GRID_SIZE: {
      return {
        ...state,
        gridSize: action.payload,
      };
    }
    case UserInterfaceActionTypes.SET_AUTO_GRID_SIZE_SWITCHING: {
      return {
        ...state,
        autoGridSizeSwitching: action.payload,
      };
    }
    case UserInterfaceActionTypes.SET_PREVIEW_VIEW_MODE: {
      return {
        ...state,
        previewViewMode: action.payload,
      };
    }
    case UserInterfaceActionTypes.SET_HAS_PROJECT_CHANGED: {
      return {
        ...state,
        projectChanged: action.payload,
      };
    }
    case UserInterfaceActionTypes.SET_CALENDAR_APPLY_LAYOUTS_WARNING: {
      return {
        ...state,
        calendarApplyLayoutsWarningDialog: action.payload,
      };
    }
    case UserInterfaceActionTypes.SET_SELECTED_SIDE: {
      return {
        ...state,
        selectedSide: action.payload,
      };
    }
    case UserInterfaceActionTypes.SET_TIP_OPEN: {
      return {
        ...state,
        tips: {...state.tips, [action.payload.name]: action.payload.open},
      };
    }
    case UserInterfaceActionTypes.SET_FROM_ASSISTANT: {
      return {
        ...state,
        fromAssistant: {...action.payload},
      };
    }
    case UserInterfaceActionTypes.SET_SWAP_ACTIVE: {
      return {
        ...state,
        swapActive: action.payload,
      };
    }
    case UserInterfaceActionTypes.SET_HISTORY_STATE: {
      return {
        ...state,
        history: {...action.payload},
      };
    }
    case UserInterfaceActionTypes.RESET_STATE: {
      return {
        ...action.payload,
      };
    }
    case UserInterfaceActionTypes.SET_INSIDE_COVER_NOT_EDITABLE_TIP: {
      return {
        ...state,
        insideCoverNotEditableTip: {...action.payload},
      };
    }
    case UserInterfaceActionTypes.SET_TEXT_IS_PROCESSING: {
      return {
        ...state,
        textIsProcessing: action.payload,
      };
    }
    case UserInterfaceActionTypes.SET_KEYBOARD_HELP: {
      return {
        ...state,
        tips: {...state.tips, keyboardHelp: {...action.payload}},
      };
    }
    case UserInterfaceActionTypes.SET_GRID_VIEW_DATA: {
      return {
        ...state,
        gridViewColumnData: {...state.gridViewColumnData, ...action.payload},
      };
    }
    default:
      return state;
  }
};

export const UserInterfaceProvider = (props: Pick<UserInterfaceState, 'children'>) => {
  const {children} = props;
  const app = useTaopix();
  const {experience} = useExperience();
  const [, startTransition] = useTransition();
  const [state, dispatch] = useReducer(userInterfaceReducer, {
    ...initialState,
  });
  const settings = useSettings();
  const [isRetroPrints, setIsRetroPrints] = useState(false);
  const storedViewMode = (sessionStorage.getItem(`${settings?.state?.tokens?.session}-viewMode`) ||
    '') as ViewMode;

  const viewMap = {
    ls: ViewMode.List,
    gv: ViewMode.Grid,
    spv: ViewMode.Single,
  };

  useEffect(() => {
    if (!app.state.booted) {
      dispatch(UserInterfaceActions.resetState(initialState));
    }
  }, [app.state.booted]);

  useEffect(() => {
    if (Object.keys(experience).length === 0) return;
    // set default project view
    startTransition(() => {
      const viewModes = experience.EDITOR.workArea.editingViewModes;
      let validViewMode = storedViewMode;
      if (('' as ViewMode) !== validViewMode) {
        if (Object.prototype.hasOwnProperty.call(viewModes, validViewMode)) {
          if (!viewModes[validViewMode]) {
            validViewMode = viewMap[experience.EDITOR.workArea.defaultView];
          }
        }
      } else {
        if (isRetroPrints) {
          validViewMode = state.isSmallScreen ? ViewMode.List : ViewMode.Grid;
        } else {
          validViewMode = viewMap[experience.EDITOR.workArea.defaultView] || ViewMode.None;
        }
      }

      dispatch(UserInterfaceActions.setViewMode(validViewMode));
    });

    // set default page lock
    dispatch(
      UserInterfaceActions.setCanEditPageLayout(experience.EDITOR.pageEditing.canEditPageLayout)
    );
  }, [experience, storedViewMode, isRetroPrints]);

  // set the naming state based on the settings from initialise
  useEffect(() => {
    app.core.get('loader').on('onInit', response => {
      dispatch(
        UserInterfaceActions.setProjectNameDialog({
          ...state.projectNaming,
          projectName: response.project.name,
          editName: response.project.editName,
        })
      );
    });

    app.core.get('loader').on('onLoad', data => {
      const {project} = data;

      if (project.layout?.retroprints) {
        setIsRetroPrints(true);
        // lock the UI so the objects can't be moved, modified via the toolpalettes
        dispatch(UserInterfaceActions.setCanEditPageLayout(true));
      }
    });
  }, [app.core.get('loader')]);

  const contextValue = useMemo(
    () => ({
      state,
      dispatch,
    }),
    [state, dispatch]
  );

  return (
    <UserInterfaceContext.Provider value={contextValue}>{children}</UserInterfaceContext.Provider>
  );
};

export const useUiState = () => {
  const context = useContext(UserInterfaceContext);
  if (context === undefined) {
    throw new Error('useUiState must be used within a UserInterfaceProvider');
  }
  return context;
};
